/**
 * Schema mapping for Supabase tables
 * Maps table names to their respective schemas
 */

export type SchemaName = 'public' | 'crm' | 'buchhaltung' | 'elearning' | 'zugang' | 'system_config' | 'commission' | 'communication' | 'content';

// Table to schema mapping based on database reorganization
const tableSchemaMap: Record<string, SchemaName> = {
  // CRM Schema - Customer relationship management
  'kunden': 'crm',
  'vertraege': 'crm',
  'fremdvertraege': 'crm',
  'aufgaben': 'public', // Use public view for proper INSERT trigger handling
  'aufgaben_shares': 'crm',
  'ansprechpartner': 'crm',
  'vertriebspartner': 'public', // Use public view for compatibility
  'interventionen': 'crm',
  'calendar_events': 'crm',
  'calendar_event_shares': 'crm',
  'vertragsnummer_historie': 'crm',
  'vbs': 'crm',
  'gesellschaften': 'crm',
  'dokument_metadaten': 'crm',
  'dokumentenarten': 'crm',
  'vertrag_provisionen': 'crm',
  'vb_templates': 'crm',
  'sparten': 'crm',
  'bereiche': 'crm',
  'hauptsparte': 'commission',
  'untersparte': 'commission',
  'detailsparte': 'commission',
  'partner_hierarchy_cache': 'crm',
  'beleg_provisionen': 'crm',
  'wissensdatenbank': 'crm',
  'vorlagen': 'crm',
  'kunden_beziehungen': 'public', // Use public view for INSTEAD OF trigger handling
  'beratung_module': 'public', // Use public view for compatibility
  'beratung_sparten': 'public', // Use public view for compatibility
  'beratung_seiten': 'public', // Use public view for compatibility
  'beratung_sessions': 'public', // Use public view for INSTEAD OF trigger handling
  'beratung_antworten': 'public', // Use public view for compatibility
  'rahmenvereinbarungen': 'public', // Use public view for compatibility
  'import_protokoll': 'crm',
  'gesellschaft_vermittlernummern': 'public', // Use public view for compatibility
  'abrechnungen': 'public',
  'kunden_partner': 'crm',
  
  // OMDS (Österreichischer Maklerdatensatz) Schema
  'omds_versicherungsunternehmen': 'crm',
  'omds_codes': 'crm',
  'omds_makler_tenant_mapping': 'crm',
  'omds_import_paket': 'crm',
  'omds_partner': 'crm',
  'omds_vertrag': 'crm',
  'omds_schaden': 'crm',
  'omds_schaden_zahlung': 'crm',
  'omds_provision': 'crm',
  'omds_mahnung': 'crm',
  'omds_vertragsfonds': 'crm',
  'omds_fonds': 'crm',
  'omds_vertragsfonds_betrag': 'crm',
  
  // Provisionsabrechnung
  'vertriebspartner_vermittlernummern': 'crm',
  'provisions_abrechnungen': 'crm',
  'provisions_abrechnung_positionen': 'crm',
  
  // Leads Schema - Use public view for proper RLS trigger handling
  'leads': 'public',
  'lead_bereiche': 'public',
  'leadhelden_lead_types': 'public',
  'leadhelden_orders': 'public',
  'leadhelden_pricing_tracking': 'public',
  'leadhelden_templates': 'public',
  
  // Buchhaltung Schema - Accounting & billing
  'buchhaltung_belege': 'buchhaltung',
  'buchhaltung_kategorien': 'buchhaltung',
  'buchhaltung_provisionen': 'buchhaltung',
  'buchhaltung_rechnungen': 'buchhaltung',
  'buchhaltung_vorlagen': 'buchhaltung',
  
  // E-Learning Schema - Use public views for INSTEAD OF trigger handling
  'elearning_kurse': 'public',
  'elearning_module': 'public',
  'elearning_einschreibungen': 'public',
  'elearning_lernpfade': 'public',
  'elearning_lernpfad_kurse': 'public',
  'elearning_modul_fortschritt': 'public',
  'elearning_zertifikate': 'public',
  'elearning_kapitel': 'public',
  'elearning_quizzes': 'public',
  'elearning_quiz_fragen': 'public',
  'elearning_quiz_antworten': 'public',
  'elearning_quiz_versuche': 'public',
  'elearning_anwesenheits_log': 'public',
  'elearning_kurs_kaeufe': 'public',
  'elearning_kurs_bewertungen': 'public',
  'elearning_lern_notizen': 'public',
  
  // Zugang Schema - Access credentials & agreements (use public views for INSTEAD OF trigger handling)
  'access_credentials': 'public',
  'access_credentials_partners': 'public',
  'access_provisionsschemas': 'public',
  'access_templates': 'public',
  'access_vermittlernummern': 'public',
  'rabatte_sideletter': 'public',
  'vermittlernummer_partners': 'public',
  'vermittlernummer_provisionsschemas': 'public',
  
  // System Config Schema - System settings & audit
  'custom_roles': 'system_config',
  'system_features': 'system_config',
  'system_modules': 'system_config',
  'notifications': 'system_config',
  'notification_preferences': 'system_config',
  'role_permissions': 'system_config',
  'user_permission_overrides': 'system_config',
  'permission_audit_log': 'system_config',
  'tenants': 'system_config',
  'user_accessible_modules': 'system_config',
  // Product management uses public views for INSTEAD OF triggers
  'produkte': 'public',
  'produkt_tarife': 'public',
  'tarif_deckungen': 'public',
  'produkt_provisionssaetze': 'public',
  
  // Commission Schema - Commission rates (use public views for compatibility)
  'provisionssatz': 'public',
  'provisionsschemas': 'public',
  'versicherungssteuer': 'public',
  'praemienzahlung': 'public',
  'provisionsschema_typ': 'public',
  'wertungsbasis': 'public',
  'stornoreserve_basis': 'public',
  'einmalzuzahlung_basis': 'public',
  'wertanpassung_basis': 'public',
  'provision_ist': 'commission',
  'rollen_provisionsmatrix': 'commission',
  'sparte_mapping': 'commission',
  
  // Communication Schema - Email management (use public views for compatibility)
  'email_accounts': 'public',
  'email_messages': 'public',
  'email_threads': 'public',
  'email_entity_links': 'public',
  'email_labels': 'public',
  'email_message_labels': 'public',
  'email_attachments': 'public',
  'email_sync_status': 'public',
  
  // Content Schema - Knowledge base & downloadcenter
  'wissensdatenbank_dokumente': 'content',
  'downloadcenter_folders': 'content',
  'downloadcenter_files': 'content',
  
  // Public Schema - Auth & core tables (remain in public)
  'chat_messages': 'public',
  'event_responses': 'public',
  'invitation': 'public',
  'news': 'public',
  'produktanbieter_favoriten': 'public',
  'upcoming_events': 'public',
  'user_roles': 'public',
  'user_sessions': 'public',
  'tenant_themes': 'public',
  'tenant_widget_layouts': 'public',
  'user_widget_layouts': 'public',
};

/**
 * Get the schema for a given table name
 * Falls back to 'public' if table is not in mapping
 */
export function getSchemaForTable(tableName: string): SchemaName {
  return tableSchemaMap[tableName] || 'public';
}

/**
 * Check if a table belongs to a specific schema
 */
export function isTableInSchema(tableName: string, schema: SchemaName): boolean {
  return getSchemaForTable(tableName) === schema;
}

/**
 * Get all tables for a given schema
 */
export function getTablesForSchema(schema: SchemaName): string[] {
  return Object.entries(tableSchemaMap)
    .filter(([_, s]) => s === schema)
    .map(([table]) => table);
}
